import React, { useMemo } from "react";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";

const buildEmptyChart = () => ({
    id: `chart-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    title: "",
    unit: "",
    locale: "en",
    is_default: false,
    sort_order: 0,
    data: {
        columns: ["Size", "Value"],
        rows: [["", ""]],
        notes: "",
    },
});

const ensureRowIntegrity = (rows = [], columnCount = 1) => {
    if (columnCount < 1) return [[""]];
    return rows.length
        ? rows.map((row) => {
            const current = Array.isArray(row) ? [...row] : [];
            if (current.length > columnCount) {
                current.length = columnCount;
                return current;
            }
            while (current.length < columnCount) {
                current.push("");
            }
            return current;
        })
        : [new Array(columnCount).fill("")];
};

const SizeChartBuilder = ({ charts = [], onChange }) => {
    const list = useMemo(() => {
        if (!Array.isArray(charts)) return [];
        return charts.map((chart, idx) => {
            const columns =
                chart?.data?.columns?.length > 0
                    ? chart.data.columns
                    : ["Size", "Value"];
            const rows = ensureRowIntegrity(chart?.data?.rows, columns.length);
            return {
                ...chart,
                id: chart.id || `chart-${idx}`,
                data: {
                    columns,
                    rows,
                    notes: chart?.data?.notes || "",
                },
            };
        });
    }, [charts]);

    const updateCharts = (next) => {
        if (typeof onChange === "function") {
            onChange(next);
        }
    };

    const handleAddChart = () => updateCharts([...list, buildEmptyChart()]);

    const handleRemoveChart = (chartId) =>
        updateCharts(list.filter((chart) => chart.id !== chartId));

    const updateChart = (chartId, updater) => {
        updateCharts(
            list.map((chart) =>
                chart.id === chartId ? { ...chart, ...updater(chart) } : chart
            )
        );
    };

    const handleMetaChange = (chartId, field, value) => {
        updateChart(chartId, () => ({ [field]: value }));
    };

    const handleIsDefaultChange = (chartId, value) => {
        updateCharts(
            list.map((chart) => ({
                ...chart,
                is_default: chart.id === chartId ? value : false,
            }))
        );
    };

    const handleColumnChange = (chartId, index, value) => {
        updateCharts(
            list.map((chart) =>
                chart.id === chartId
                    ? {
                        ...chart,
                        data: {
                            ...chart.data,
                            columns: chart.data.columns.map((col, idx) =>
                                idx === index ? value : col
                            ),
                            rows: ensureRowIntegrity(chart.data.rows, chart.data.columns.length),
                        },
                    }
                    : chart
            )
        );
    };

    const handleAddColumn = (chartId) => {
        updateCharts(
            list.map((chart) =>
                chart.id === chartId
                    ? {
                        ...chart,
                        data: {
                            ...chart.data,
                            columns: [...chart.data.columns, `Column ${chart.data.columns.length + 1}`],
                            rows: ensureRowIntegrity(chart.data.rows, chart.data.columns.length + 1),
                        },
                    }
                    : chart
            )
        );
    };

    const handleRemoveColumn = (chartId, colIdx) => {
        updateCharts(
            list.map((chart) => {
                if (chart.id !== chartId || chart.data.columns.length <= 1) return chart;
                const nextColumns = chart.data.columns.filter((_, idx) => idx !== colIdx);
                const nextRows = chart.data.rows.map((row) =>
                    row.filter((_, idx) => idx !== colIdx)
                );
                return {
                    ...chart,
                    data: {
                        ...chart.data,
                        columns: nextColumns,
                        rows: ensureRowIntegrity(nextRows, nextColumns.length),
                    },
                };
            })
        );
    };

    const handleRowValueChange = (chartId, rowIdx, colIdx, value) => {
        updateCharts(
            list.map((chart) =>
                chart.id === chartId
                    ? {
                        ...chart,
                        data: {
                            ...chart.data,
                            rows: chart.data.rows.map((row, idx) =>
                                idx === rowIdx
                                    ? row.map((cell, cIdx) => (cIdx === colIdx ? value : cell))
                                    : row
                            ),
                        },
                    }
                    : chart
            )
        );
    };

    const handleAddRow = (chartId) => {
        updateCharts(
            list.map((chart) =>
                chart.id === chartId
                    ? {
                        ...chart,
                        data: {
                            ...chart.data,
                            rows: [
                                ...chart.data.rows,
                                new Array(chart.data.columns.length).fill(""),
                            ],
                        },
                    }
                    : chart
            )
        );
    };

    const handleRemoveRow = (chartId, rowIdx) => {
        updateCharts(
            list.map((chart) => {
                if (chart.id !== chartId || chart.data.rows.length <= 1) return chart;
                const nextRows = chart.data.rows.filter((_, idx) => idx !== rowIdx);
                return {
                    ...chart,
                    data: {
                        ...chart.data,
                        rows: ensureRowIntegrity(nextRows, chart.data.columns.length),
                    },
                };
            })
        );
    };

    const handleNotesChange = (chartId, value) => {
        updateCharts(
            list.map((chart) =>
                chart.id === chartId
                    ? {
                        ...chart,
                        data: {
                            ...chart.data,
                            notes: value,
                        },
                    }
                    : chart
            )
        );
    };

    return (
        <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Size Charts</h5>
                <Button variant="outline-secondary" size="sm" onClick={handleAddChart}>
                    + Add Chart
                </Button>
            </Card.Header>
            <Card.Body>
                {!list.length && (
                    <div className="text-center text-muted">
                        <p className="mb-2">No size chart configured yet.</p>
                        <Button variant="secondary" onClick={handleAddChart}>
                            Create first size chart
                        </Button>
                    </div>
                )}

                {list.map((chart, index) => (
                    <div key={chart.id || index} className="border rounded p-3 mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Chart #{index + 1}</h6>
                            <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0"
                                onClick={() => handleRemoveChart(chart.id)}
                            >
                                Remove
                            </Button>
                        </div>
                        <Row className="g-3">
                            <Col lg={4} md={6}>
                                <Form.Group>
                                    <Form.Label>Title</Form.Label>
                                    <Form.Control
                                        value={chart.title || ""}
                                        onChange={(e) =>
                                            handleMetaChange(chart.id, "title", e.target.value)
                                        }
                                        placeholder="Ring Size Chart"
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={2} md={6}>
                                <Form.Group>
                                    <Form.Label>Unit</Form.Label>
                                    <Form.Control
                                        value={chart.unit || ""}
                                        onChange={(e) =>
                                            handleMetaChange(chart.id, "unit", e.target.value)
                                        }
                                        placeholder="cm"
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={2} md={6}>
                                <Form.Group>
                                    <Form.Label>Locale</Form.Label>
                                    <Form.Control
                                        value={chart.locale || ""}
                                        onChange={(e) =>
                                            handleMetaChange(chart.id, "locale", e.target.value)
                                        }
                                        placeholder="en"
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={2} md={6}>
                                <Form.Group>
                                    <Form.Label>Sort Order</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={chart.sort_order ?? index}
                                        onChange={(e) =>
                                            handleMetaChange(chart.id, "sort_order", Number(e.target.value))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={2} md={6} className="d-flex align-items-center">
                                <Form.Check
                                    type="switch"
                                    id={`is-default-${chart.id}`}
                                    label="Default"
                                    checked={!!chart.is_default}
                                    onChange={(e) => handleIsDefaultChange(chart.id, e.target.checked)}
                                />
                            </Col>
                        </Row>

                        <div className="table-responsive mt-3">
                            <Table bordered size="sm" className="mb-2">
                                <thead>
                                    <tr>
                                        {chart.data?.columns?.map((column, colIdx) => (
                                            <th key={`${chart.id}-column-${colIdx}`}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Form.Control
                                                        value={column}
                                                        onChange={(e) =>
                                                            handleColumnChange(chart.id, colIdx, e.target.value)
                                                        }
                                                        placeholder={`Column ${colIdx + 1}`}
                                                    />
                                                    {chart.data.columns.length > 1 && (
                                                        <Button
                                                            variant="light"
                                                            size="sm"
                                                            onClick={() => handleRemoveColumn(chart.id, colIdx)}
                                                        >
                                                            &times;
                                                        </Button>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                        <th style={{ width: 120 }}>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => handleAddColumn(chart.id)}
                                            >
                                                + Column
                                            </Button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chart.data?.rows?.map((row, rowIdx) => (
                                        <tr key={`${chart.id}-row-${rowIdx}`}>
                                            {row.map((cell, cellIdx) => (
                                                <td key={`${chart.id}-cell-${rowIdx}-${cellIdx}`}>
                                                    <Form.Control
                                                        value={cell}
                                                        onChange={(e) =>
                                                            handleRowValueChange(
                                                                chart.id,
                                                                rowIdx,
                                                                cellIdx,
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="Value"
                                                    />
                                                </td>
                                            ))}
                                            <td>
                                                {chart.data.rows.length > 1 && (
                                                    <Button
                                                        variant="light"
                                                        size="sm"
                                                        onClick={() => handleRemoveRow(chart.id, rowIdx)}
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleAddRow(chart.id)}
                            >
                                + Row
                            </Button>
                        </div>

                        <Form.Group className="mt-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={chart.data?.notes || ""}
                                onChange={(e) => handleNotesChange(chart.id, e.target.value)}
                                placeholder="Any helpful description"
                            />
                        </Form.Group>
                    </div>
                ))}
            </Card.Body>
        </Card>
    );
};

export default SizeChartBuilder;
export { buildEmptyChart };

